import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { initializeApp } from 'firebase/app';
import { 
    getAuth, 
    onAuthStateChanged, 
    signOut, 
    GoogleAuthProvider
} from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, onSnapshot, collection } from 'firebase/firestore';
import { momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { initialPlaybooks } from './data/mock';
import LoginScreen from './components/LoginScreen';
import useIsMobile from './hooks/useIsMobile';
import TaskCreationModal from './components/TaskCreationModal';

// Lazy-loaded components
const DashboardView = lazy(() => import('./views/DashboardView'));
const ThreatIntelView = lazy(() => import('./views/ThreatIntelView'));
const PlaybooksView = lazy(() => import('./views/PlaybooksView'));
const ComplianceView = lazy(() => import('./views/ComplianceView'));
const AssetView = lazy(() => import('./views/AssetView'));
const ApprovalsView = lazy(() => import('./views/ApprovalsView'));
const CalendarView = lazy(() => import('./views/CalendarView'));
const ReportsView = lazy(() => import('./views/ReportsView'));


// --- Add this CSS directly into your project's main CSS file (e.g., App.css or index.css) ---
const GlobalStyles = () => (
  <style>{`
    body { font-family: 'Inter', sans-serif; }
    .login-body { background-color: #0a0c1f !important; color: #e5e7eb; }
    .dashboard-body { background-color: #0a0c1f !important; color: #e5e7eb; }
    .login-container { background: rgba(10, 12, 31, 0.6); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); border: 1px solid rgba(0, 191, 255, 0.1); }
    .form-input { color: white; background-color: rgba(255, 255, 255, 0.05); border: 1px solid rgba(0, 191, 255, 0.2); transition: all 0.3s ease; }
    .form-input:focus { background-color: rgba(255, 255, 255, 0.1); border-color: #00bfff; box-shadow: 0 0 15px rgba(0, 191, 255, 0.3); }
    .login-button { background: #00bfff; box-shadow: 0 0 20px rgba(0, 191, 255, 0.4); transition: all 0.3s ease; }
    .login-button:hover { background: #00a8e6; box-shadow: 0 0 30px rgba(0, 191, 255, 0.7); transform: translateY(-2px); }
    .google-button { background-color: rgba(255, 255, 255, 0.05); border: 1px solid rgba(0, 191, 255, 0.2); transition: all 0.3s ease; }
    .google-button:hover { background-color: rgba(255, 255, 255, 0.1); border-color: #00bfff; }
     .nav-tab { transition: all 0.2s; }
     .nav-tab.active { border-bottom-color: #38bdf8; color: #38bdf8; font-weight: 600; }
     .clickable-item { cursor: pointer; text-decoration: underline; color: #38bdf8; }
     .clickable-item:hover { color: #7dd3fc; }
     .task-incident-response { color: #8b5cf6; }
     .task-vulnerability { color: #ef4444; }
     .task-update { color: #f97316; }
     .task-compliance { color: #ef4444; }
     .task-status-completed { color: #22c55e; }
     .task-status-in-progress { color: #3b82f6; }
     .task-status-pending, .approval-status-pending-approval { color: #f59e0b; }
     .approval-status-approved { color: #22c55e; }
     .approval-status-rejected { color: #ef4444; }
     .accordion-content { max-height: 0; overflow: hidden; transition: max-height 0.3s ease-in-out; }
    
    /* General Calendar Styling */
    .rbc-calendar {
        color: #e5e7eb; /* Light gray text for dates */
        background-color: transparent;
    }
    .rbc-header {
        background-color: rgba(10, 12, 31, 0.4);
        border: 1px solid rgba(0, 191, 255, 0.2);
        color: #38bdf8; /* Sky blue for day headers (Sun, Mon, etc.) */
        padding: 10px 0;
    }
    /* Toolbar Styling */
    .rbc-toolbar {
        background-color: transparent;
        border-bottom: 1px solid rgba(0, 191, 255, 0.2);
        color: #e5e7eb;
        padding: 10px 0;
        margin-bottom: 10px;
    }
    .rbc-toolbar .rbc-toolbar-label {
        color: #e5e7eb;
        font-size: 1.5em;
        font-weight: bold;
    }
    .rbc-toolbar button {
        color: #e5e7eb !important;
        background-color: rgba(255, 255, 255, 0.05) !important;
        border: 1px solid rgba(0, 191, 255, 0.2) !important;
        transition: all 0.3s ease;
    }
    .rbc-toolbar button:hover, .rbc-toolbar button:focus {
        color: white !important;
        background-color: rgba(0, 191, 255, 0.2) !important;
        border-color: #00bfff !important;
    }
    .rbc-toolbar .rbc-active button {
        background-color: #00bfff !important;
        box-shadow: 0 0 10px rgba(0, 191, 255, 0.5);
    }
    /* Calendar Grid Styling */
    .rbc-month-view, .rbc-time-view, .rbc-agenda-view {
        background-color: rgba(10, 12, 31, 0.6);
        border: 1px solid rgba(0, 191, 255, 0.2) !important;
    }
    .rbc-day-bg, .rbc-month-row, .rbc-time-slot, .rbc-time-gutter, .rbc-timeslot-group {
        background-color: transparent !important;
        border-color: rgba(0, 191, 255, 0.1) !important;
    }
    .rbc-off-range-bg {
        background-color: rgba(0, 0, 0, 0.3) !important; /* Darken days outside the current month */
    }
    .rbc-today {
        background-color: rgba(0, 191, 255, 0.15) !important; /* Highlight current day */
    }
    /* Event Styling */
    .rbc-event, .rbc-day-slot .rbc-background-event {
        background-color: #00bfff !important;
        border: 1px solid #0ea5e9 !important;
        color: #0a0c1f !important; /* Dark text on events for contrast */
        border-radius: 4px;
    }
    .rbc-show-more {
        background-color: rgba(0, 191, 255, 0.2);
        color: #e5e7eb;
        transition: all 0.3s ease;
    }
    .rbc-show-more:hover {
        background-color: rgba(0, 191, 255, 0.4);
    }
    
  `}</style>
);


// --- Firebase Configuration ---
const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID
};

// --- Initialize Firebase ---
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const localizer = momentLocalizer(moment);
export const googleProvider = new GoogleAuthProvider();

const FuturisticBackground = () => {
    useEffect(() => {
        const canvas = document.getElementById('background-canvas');
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        let particlesArray;

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            initAndDraw();
        };

        class Particle {
            constructor(x, y, dX, dY, s) { this.x=x; this.y=y; this.directionX=dX; this.directionY=dY; this.size=s; }
            draw() { ctx.beginPath(); ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2); ctx.fillStyle = 'rgba(0, 191, 255, 0.5)'; ctx.fill(); }
        }

        function initAndDraw() {
            particlesArray = [];
            let num = (window.innerWidth * window.innerHeight) / 15000;
            for (let i = 0; i < num; i++) {
                let s = (Math.random() * 2) + 0.5,
                    x = Math.random() * (window.innerWidth - s * 2) + s * 2,
                    y = Math.random() * (window.innerHeight - s * 2) + s * 2,
                    dX = (Math.random() * 0.4) - 0.2,
                    dY = (Math.random() * 0.4) - 0.2;
                particlesArray.push(new Particle(x, y, dX, dY, s));
            }
            drawParticles();
        }

        function drawParticles() {
            ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
            for (let i = 0; i < particlesArray.length; i++) {
                particlesArray[i].draw();
            }
        }

        resizeCanvas(); // Initial setup
        window.addEventListener('resize', resizeCanvas);

        return () => {
            window.removeEventListener('resize', resizeCanvas);
        };
    }, []);

    return <canvas id="background-canvas" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: -10, opacity: 0.3 }}></canvas>;
};

// --- Main App Component ---
export default function App() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('dashboard');
    const [tasks, setTasks] = useState([]);
    const [tasksLoading, setTasksLoading] = useState(true);
    const [playbooks, setPlaybooks] = useState(initialPlaybooks);
    const [feedUrls, setFeedUrls] = useState([]);
    const [assets, setAssets] = useState([]);
    const [assetsLoading, setAssetsLoading] = useState(true);
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
    const [taskData, setTaskData] = useState(null);
    const isMobile = useIsMobile(768);

    const seedInitialData = useCallback(async (uid) => {
        const playbookDocRef = doc(db, `users/${uid}/playbooks/all`);
        const feedsDocRef = doc(db, `users/${uid}/settings/feeds`);
        const playbookSnap = await getDoc(playbookDocRef);
        const feedsSnap = await getDoc(feedsDocRef);
        if (!playbookSnap.exists()) {
            await setDoc(playbookDocRef, initialPlaybooks);
        }
        if (!feedsSnap.exists()) {
            await setDoc(feedsDocRef, { urls: ['https://feeds.feedburner.com/TheHackersNews', 'https://www.bleepingcomputer.com/feed/'] });
        }
    }, []);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setUser(user);
                 document.body.className = 'dashboard-body';
                seedInitialData(user.uid);
            } else {
                setUser(null);
                 document.body.className = 'login-body';
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, [seedInitialData]);
    
    useEffect(() => {
        if (!user) return;
        // set per-dataset loading before subscribing
        setTasksLoading(true); setAssetsLoading(true);
        let firstTasks = true, firstAssets = true;
        const unsubTasks = onSnapshot(collection(db, `users/${user.uid}/tasks`), s => {
            setTasks(s.docs.map(d => {
                const data = d.data();
                return {
                    ...data, 
                    id: d.id, 
                    dueDate: data.dueDate?.toDate ? data.dueDate.toDate() : (data.dueDate ? new Date(data.dueDate) : null), 
                    createdDate: data.createdDate?.toDate ? data.createdDate.toDate() : (data.createdDate ? new Date(data.createdDate) : null)
                };
            }));
            if (firstTasks) { setTasksLoading(false); firstTasks = false; }
        });
        const unsubPlaybooks = onSnapshot(doc(db, `users/${user.uid}/playbooks/all`), s => { if (s.exists()) { setPlaybooks(s.data()); } });
        const unsubFeeds = onSnapshot(doc(db, `users/${user.uid}/settings/feeds`), s => { if (s.exists()) { setFeedUrls(s.data().urls); } });
        const unsubAssets = onSnapshot(collection(db, `users/${user.uid}/assets`), s => { setAssets(s.docs.map(d => ({...d.data(), id:d.id}))); if (firstAssets) { setAssetsLoading(false); firstAssets = false; }});
        const unsubCalendarEvents = onSnapshot(collection(db, `users/${user.uid}/calendarEvents`), s => {
            setCalendarEvents(s.docs.map(d => {
                const data = d.data();
                return {
                    ...data,
                    start: data.start?.toDate ? data.start.toDate() : new Date(data.start),
                    end: data.end?.toDate ? data.end.toDate() : new Date(data.end),
                    id: d.id
                };
            }));
        });

        return () => { unsubTasks(); unsubPlaybooks(); unsubFeeds(); unsubAssets(); unsubCalendarEvents(); };
    }, [user]);

    const handleSignOut = async () => { try { await signOut(auth); } catch (e) { console.error("Sign out error", e); }};

    const [calendarEvents, setCalendarEvents] = useState([]);

    const openTaskModal = (data) => {
        setTaskData(data);
        setIsTaskModalOpen(true);
    };

    return (
        <>
            <GlobalStyles />
            <FuturisticBackground/>
            {loading && (
              <div className="h-screen flex flex-col items-center justify-center text-white bg-[#0a0c1f] gap-3">
                <div className="inline-block border-4 border-sky-400/30 border-t-sky-400 rounded-full animate-spin" style={{ width: 48, height: 48 }} />
                <div className="text-sm text-gray-400">Initializing session…</div>
              </div>
            )}
            {!loading && !user && <LoginScreen />}
            {!loading && user && (
                 <div className="relative z-10">
                    <nav className="sticky top-0 z-20" style={{ background: 'rgba(10, 12, 31, 0.8)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)'}}>
                         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="flex items-center justify-between h-16">
                                <div className="flex items-center">
                                    <img src="/logo.svg" alt="misecure logo" className="h-8 w-8 mr-3" />
                                    <h1 className="text-2xl font-bold text-white">mi<span className="text-sky-400">secure</span>.my</h1>
                                </div>
                                <div className="flex items-center gap-4">
                                    <span className="text-sm text-gray-400 hidden md:block">{user.email || user.displayName || 'Anonymous User'}</span>
                                    <button onClick={handleSignOut} className="text-sm bg-red-500/80 hover:bg-red-500 text-white font-bold py-1 px-3 rounded">Logout</button>
                                </div>
                            </div>
                            {/* Desktop tabs */}
                            <div className="hidden md:flex border-b border-sky-500/20 overflow-x-auto">
                               {['dashboard', 'threat-intel', 'incident-response', 'compliance', 'asset-patch-mgmt', 'approvals', 'calendar', 'reports'].map(tabId => (
                                   <button key={tabId} id={`${tabId}-tab`} className={`nav-tab flex-shrink-0 py-3 px-4 text-sm font-medium border-b-2 border-transparent text-gray-400 hover:text-sky-300 capitalize ${activeTab === tabId ? 'active' : ''}`} onClick={() => setActiveTab(tabId)}>{tabId.replace('-', ' ')}</button>
                               ))}
                            </div>
                        </div>
                    </nav>
                    <main className={`p-4 sm:p-6 lg:p-8 ${isMobile ? 'pb-20' : ''}`}>
                        <Suspense fallback={
                          <div className="space-y-6">
                            <div className="skeleton h-8 w-48" />
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              <div className="p-4 rounded-lg bg-[rgba(10,12,31,0.6)] border border-sky-500/10">
                                <div className="space-y-3">
                                  <div className="skeleton h-5 w-40" />
                                  {[...Array(5)].map((_, i) => <div key={i} className="skeleton h-4 w-full" />)}
                                </div>
                              </div>
                              <div className="p-4 rounded-lg bg-[rgba(10,12,31,0.6)] border border-sky-500/10">
                                <div className="space-y-3">
                                  <div className="skeleton h-5 w-40" />
                                  {[...Array(5)].map((_, i) => <div key={i} className="skeleton h-4 w-full" />)}
                                </div>
                              </div>
                            </div>
                          </div>
                        }>
                            {activeTab === 'dashboard' && <DashboardView setActiveTab={setActiveTab} user={user} isMobile={isMobile} tasks={tasks} assets={assets} />}
                            {activeTab === 'threat-intel' && <ThreatIntelView feedUrls={feedUrls} user={user} isMobile={isMobile} />}
                            {activeTab === 'incident-response' && <PlaybooksView playbooks={playbooks} user={user} isMobile={isMobile} />}
                            {activeTab === 'compliance' && <ComplianceView assets={assets} user={user} openTaskModal={openTaskModal} tasks={tasks} isMobile={isMobile} assetsLoading={assetsLoading} />}
                            {activeTab === 'asset-patch-mgmt' && <AssetView assets={assets} user={user} openTaskModal={openTaskModal} isMobile={isMobile} assetsLoading={assetsLoading} />}
                            {activeTab === 'approvals' && <ApprovalsView tasks={tasks} user={user} isMobile={isMobile} tasksLoading={tasksLoading} />}
                            {activeTab === 'calendar' && <CalendarView events={calendarEvents} tasks={tasks} user={user} isMobile={isMobile} />}
                            {activeTab === 'reports' && <ReportsView tasks={tasks} events={calendarEvents} isMobile={isMobile} />}
                        </Suspense>
                    </main>
                    {/* Mobile bottom nav */}
                    {isMobile && (
                      <MobileBottomNav activeTab={activeTab} setActiveTab={setActiveTab} />
                    )}
                     <TaskCreationModal show={isTaskModalOpen} onClose={() => setIsTaskModalOpen(false)} taskData={taskData} user={user} />
                </div>
            )}
        </>
    );
}

function MobileBottomNav({ activeTab, setActiveTab }) {
  const tabs = [
    { id: 'dashboard', label: 'Home' },
    { id: 'threat-intel', label: 'Threat' },
    { id: 'asset-patch-mgmt', label: 'Assets' },
    { id: 'compliance', label: 'Compliance' },
    { id: 'approvals', label: 'Approvals' },
    { id: 'calendar', label: 'Calendar' },
  ];
  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-50"
      style={{ background: 'rgba(10, 12, 31, 0.9)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
      aria-label="Mobile navigation"
    >
      <div
        className="flex gap-2 overflow-x-auto px-2 border-t border-sky-500/20"
        style={{ paddingTop: '8px', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)' }}
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            className={`flex-shrink-0 px-3 py-2 text-xs font-medium rounded ${activeTab === t.id ? 'text-sky-300 bg-sky-900/20' : 'text-gray-400'}`}
            onClick={() => setActiveTab(t.id)}
            aria-current={activeTab === t.id ? 'page' : undefined}
          >
            {t.label}
          </button>
        ))}
      </div>
    </nav>
  );
}
